/**
 * ConsolePanel - Collapsible log viewer for Worker console output.
 *
 * Props:
 *   logs    {Array<{level, args}>}
 *   onClear {fn}
 */

import { useState } from 'react';

const LEVEL_STYLES = {
  log:   { border: 'border-l-gray-400',   bg: '',               text: 'text-gray-200',  label: '' },
  info:  { border: 'border-l-blue-400',   bg: 'bg-blue-950/20', text: 'text-blue-200',  label: 'info' },
  warn:  { border: 'border-l-yellow-400', bg: 'bg-yellow-950/20',text: 'text-yellow-200',label: 'warn' },
  error: { border: 'border-l-red-400',    bg: 'bg-red-950/20',  text: 'text-red-200',   label: 'error' },
};

function renderArg(arg, idx) {
  if (arg === null) return <span key={idx} className="text-gray-400">null</span>;
  if (arg === undefined) return <span key={idx} className="text-gray-400">undefined</span>;
  if (typeof arg === 'string') return <span key={idx}>{arg}</span>;
  if (typeof arg === 'number' || typeof arg === 'boolean') {
    return <span key={idx} className="text-blue-300">{String(arg)}</span>;
  }
  // Object/array — render as JSON
  try {
    return (
      <span key={idx} className="text-green-300 whitespace-pre-wrap break-all">
        {JSON.stringify(arg, null, 2)}
      </span>
    );
  } catch {
    return <span key={idx} className="text-gray-400">[unserializable]</span>;
  }
}

export default function ConsolePanel({ logs = [], onClear }) {
  const [collapsed, setCollapsed] = useState(false);

  if (logs.length === 0) return null;

  const errorCount = logs.filter((l) => l.level === 'error').length;
  const warnCount = logs.filter((l) => l.level === 'warn').length;

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-gray-800 cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-300">Console</span>
          <span className="text-xs bg-gray-600 text-gray-200 rounded px-1.5 py-0.5">
            {logs.length}
          </span>
          {errorCount > 0 && (
            <span className="text-xs bg-red-600 text-white rounded px-1.5 py-0.5">
              {errorCount} error{errorCount > 1 ? 's' : ''}
            </span>
          )}
          {warnCount > 0 && (
            <span className="text-xs bg-yellow-600 text-white rounded px-1.5 py-0.5">
              {warnCount} warn{warnCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onClear && (
            <button
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="text-xs text-gray-400 hover:text-gray-200 transition px-1"
            >
              Clear
            </button>
          )}
          <span className="text-gray-400 text-xs">{collapsed ? '▶' : '▼'}</span>
        </div>
      </div>

      {/* Log entries */}
      {!collapsed && (
        <div className="max-h-48 overflow-y-auto divide-y divide-gray-800">
          {logs.map((log, i) => {
            const style = LEVEL_STYLES[log.level] || LEVEL_STYLES.log;
            return (
              <div
                key={i}
                className={`flex gap-2 px-3 py-1.5 text-xs font-mono border-l-2 ${style.border} ${style.bg}`}
              >
                {style.label && (
                  <span className={`shrink-0 font-bold ${style.text}`}>{style.label}</span>
                )}
                <span className={`flex-1 ${style.text} break-all`}>
                  {(log.args || []).map((arg, ai) => (
                    <span key={ai}>
                      {ai > 0 && ' '}
                      {renderArg(arg, ai)}
                    </span>
                  ))}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
